import type { RefObject } from 'react';
import { useEffect, useRef, useState, useCallback } from 'react';

type Size = {
  width?: number;
  height?: number;
};

type UseResizeObserverOptions<T extends HTMLElement> = {
  ref?: RefObject<T | null>;
  onResize?: (size: Size) => void;
  round?: (n: number) => number;
};

type UseResizeObserverResult<T extends HTMLElement> = {
  ref: (element: T | null) => void;
  width?: number;
  height?: number;
};

/**
 * A React hook that observes element size changes using ResizeObserver.
 *
 * Supports two usage patterns:
 * 1. With onResize callback - hook won't cause re-renders, just calls the callback
 * 2. Without onResize - hook returns width/height and re-renders on size changes
 *
 * @example
 * // Pattern 1: With callback (no re-renders from size changes)
 * const myRef = useRef<HTMLDivElement>(null);
 * useResizeObserver({
 *   ref: myRef,
 *   onResize: ({ width, height }) => console.log(width, height),
 * });
 *
 * @example
 * // Pattern 2: Get dimensions directly
 * const { ref, width, height } = useResizeObserver<HTMLDivElement>();
 * return <div ref={ref}>Size: {width}x{height}</div>;
 */
export function useResizeObserver<T extends HTMLElement = HTMLElement>(
  options: UseResizeObserverOptions<T> = {},
): UseResizeObserverResult<T> {
  const { ref: customRef, onResize, round = Math.round } = options;

  const internalRef = useRef<T | null>(null);
  const [size, setSize] = useState<Size>({ width: undefined, height: undefined });

  // Track if we're using callback mode to avoid unnecessary state updates
  const onResizeRef = useRef(onResize);
  onResizeRef.current = onResize;

  // RefCallback that stores the element reference
  const refCallback = useCallback((element: T | null) => {
    internalRef.current = element;
  }, []);

  useEffect(() => {
    // SSR guard - ResizeObserver doesn't exist on server
    if (typeof ResizeObserver === 'undefined') {
      return;
    }

    const element = customRef?.current ?? internalRef.current;
    if (!element) {
      return;
    }

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;

      const { width, height } = entry.contentRect;
      const roundedSize = {
        width: round(width),
        height: round(height),
      };

      if (onResizeRef.current) {
        // Callback mode - don't update state, just call the callback
        onResizeRef.current(roundedSize);
      } else {
        // State mode - update state to trigger re-render
        setSize((prevSize) => {
          if (prevSize.width === roundedSize.width && prevSize.height === roundedSize.height) {
            return prevSize;
          }
          return roundedSize;
        });
      }
    });

    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [customRef, round]);

  // When using onResize callback, don't return size values
  if (onResize) {
    return { ref: refCallback };
  }

  return {
    ref: refCallback,
    width: size.width,
    height: size.height,
  };
}

export default useResizeObserver;
